import config from 'src/config';
import { ResponseDto } from 'src/dtos/common/responseDto';
import { getData, patchData } from 'src/utils/axios/serverHelper';
import {
    HRM1030_R01Dto,
    HRM1030_R01FileDto,
    HRM1030_R01HeaderDto,
    HRM1030_R01TrainingDto,
} from './Dto_HRM1030_R01';

/** HRM1030_R01 화면의 API 기본 경로이다. */
const BASE_URL = `${config.apiUrl}/HRM1030_R01`;

/** 사원 목록·Excel 요청에 보내는 검색 조건이다. 빈 값은 undefined로 넘겨 요청에서 뺀다. */
export interface HRM1030_R01Params {
    trainYear?: string; // 교육년도 YYYY
    trainType?: string; // 교육구분
    compCd?: string; // 사업장
    orgCd?: string; // 부서
    empSts?: string; // 재직상태
    keyword?: string; // 사원번호 또는 이름
}

/**
 * 조건에 맞는 사원 목록을 교육 집계와 함께 가져온다.
 * @param option search(새 조회) 또는 refetch(저장 후 재조회)
 * @param params 검색 조건
 * @param listLength 요청에 포함하지만 현재 백엔드 목록 쿼리에서는 사용하지 않는 목록 길이(HRM 화면 공통 형태)
 * @returns 사원 목록 응답
 */
export const getHRM1030_R01List = async (option: string, params: HRM1030_R01Params, listLength = 0) =>
    await getData<ResponseDto<HRM1030_R01HeaderDto[]>>(`${BASE_URL}/header/${option}`, { params: { ...params, listLength } });

/**
 * 조건에 맞는 사원 수를 가져온다.
 * @param params 목록 조회와 같은 검색 조건
 * @returns 전체 사원 수 응답
 */
export const getHRM1030_R01AllCount = async (params: HRM1030_R01Params) =>
    await getData<ResponseDto<number>>(`${BASE_URL}/header/allCount`, { params: { ...params } });

/**
 * 사원의 교육훈련 이력을 가져온다.
 * @param empNo 사원번호. 경로에 들어가므로 특수문자를 인코딩한다.
 * @param params 교육년도·교육구분 조건
 * @returns 교육 이력 응답
 */
export const getHRM1030_R01TrainingList = async (empNo: string, params: Pick<HRM1030_R01Params, 'trainYear' | 'trainType'>) =>
    await getData<ResponseDto<HRM1030_R01TrainingDto[]>>(`${BASE_URL}/training/${encodeURIComponent(empNo)}`, { params: { ...params } });

/**
 * 교육 한 건의 수료증 첨부 목록을 가져온다.
 * @param empNo 사원번호
 * @param trainSeq 교육 순번
 * @returns 첨부 목록 응답
 */
export const getHRM1030_R01FileList = async (empNo: string, trainSeq: number) =>
    await getData<ResponseDto<HRM1030_R01FileDto[]>>(`${BASE_URL}/file/${encodeURIComponent(empNo)}/${trainSeq}`);

/**
 * 저장된 수료증 파일을 Blob으로 받는다(EQM3020과 같은 responseType).
 * @param fileId 공통 파일ID (첨부 행의 filePath 값)
 * @returns 파일 Blob 응답
 */
export const downloadHRM1030_R01File = async (fileId: string) =>
    await getData<Blob>(`${BASE_URL}/file/download/${encodeURIComponent(fileId)}`, { responseType: 'blob' });

/**
 * 사원 한 명의 교육·첨부 변경을 저장한다.
 * @param data 사원번호, 등록·수정 교육, 삭제 교육
 * @returns 저장 결과 응답
 */
export const patchHRM1030_R01 = async (data: HRM1030_R01Dto) =>
    await patchData<HRM1030_R01Dto, ResponseDto<HRM1030_R01Dto>>(BASE_URL, data);

/**
 * 사원 목록 Excel을 받는다.
 * @param params 사원 목록과 같은 검색 조건
 * @returns Excel 파일 데이터 응답
 */
export const downloadExcel_HRM1030_R01 = async (params: HRM1030_R01Params) =>
    await getData<ResponseDto<string>>(`${BASE_URL}/excel/search`, { params: { ...params } });

/**
 * 사원의 교육훈련 이력 Excel을 받는다.
 * @param empNo 사원번호
 * @param params 교육년도·교육구분 조건
 * @returns Excel 파일 데이터 응답
 */
export const downloadExcel_HRM1030_R01Training = async (empNo: string, params: Pick<HRM1030_R01Params, 'trainYear' | 'trainType'>) =>
    await getData<ResponseDto<string>>(`${BASE_URL}/training/${encodeURIComponent(empNo)}/excel`, { params: { ...params } });
